import { useState, useCallback } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface ProductUploadProps {
  imageUrl: string;
  onImageChange: (url: string) => void;
}

export function ProductUpload({ imageUrl, onImageChange }: ProductUploadProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      convertToBase64(file);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      convertToBase64(file);
    }
  }, []);

  const convertToBase64 = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      onImageChange(result);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    onImageChange("");
  };

  if (imageUrl) {
    return (
      <div className="relative">
        <div className="relative aspect-square max-w-md mx-auto rounded-xl overflow-hidden border-2 border-border bg-secondary/30">
          <img
            src={imageUrl}
            alt="Product"
            className="w-full h-full object-contain"
          />
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2"
            onClick={clearImage}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-center text-sm text-muted-foreground mt-2">
          Image produit chargée
        </p>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={cn(
        "relative aspect-square max-w-md mx-auto rounded-xl border-2 border-dashed transition-all duration-200",
        "flex flex-col items-center justify-center gap-4 p-8",
        isDragging
          ? "border-primary bg-primary/10"
          : "border-border bg-secondary/30 hover:border-primary/50 hover:bg-secondary/50"
      )}
    >
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
        <Upload className="w-8 h-8 text-primary" />
      </div>
      <div className="text-center">
        <p className="font-medium">Glissez votre image produit ici</p>
        <p className="text-sm text-muted-foreground mt-1">
          ou cliquez pour parcourir
        </p>
      </div>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <ImageIcon className="w-4 h-4" />
        <span>PNG, JPG, WEBP jusqu'à 10MB</span>
      </div>
    </div>
  );
}
